import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Avatar,
  Dropdown,
  Select,
  InputNumber,
  Switch
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LogoutOutlined,
  BookOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export default function TrainingManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trainings');
      console.log('Fetched trainings:', response.data); // Debug log to check is_active field
      setTrainings(response.data);
    } catch (error) {
      message.error('Failed to load trainings');
      console.error('Error fetching trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/super-admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleCreate = () => {
    setEditingTraining(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    console.log('Editing record:', record); // Debug log
    setEditingTraining(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      type: record.type,
      is_active: record.is_active !== undefined ? record.is_active : true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (trainingId) => {
    try {
      await api.delete(`/trainings/${trainingId}`);
      message.success('Training deleted successfully');
      fetchTrainings();
    } catch (error) {
      message.error('Failed to delete training');
      console.error('Error deleting training:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingTraining) {
        // Update existing training
        await api.put(`/trainings/${editingTraining.id}`, values);
        message.success('Training updated successfully');
      } else {
        // Create new training
        await api.post('/trainings', values);
        message.success('Training created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchTrainings();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(editingTraining ? 'Failed to update training' : 'Failed to create training');
      console.error('Error saving training:', error);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingTraining(null);
  };

  const typeColors = {
    self_paced: 'blue',
    classroom: 'orange'
  };

  const columns = [
    {
      title: 'Training',
      key: 'training',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<BookOutlined />} />
          <div>
            <div><Text strong>{record.name}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.description?.substring(0, 60)}...</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={typeColors[type]}>
          {type === 'self_paced' ? 'Self-Paced' : 'Classroom'}
        </Tag>
      ),
      filters: [
        { text: 'Self-Paced', value: 'self_paced' },
        { text: 'Classroom', value: 'classroom' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Labs',
      dataIndex: 'lab_count',
      key: 'lab_count',
      render: (count) => (
        <Space>
          <BookOutlined style={{ color: '#faad14' }} />
          <Text>{count || 0} labs</Text>
        </Space>
      ),
      sorter: (a, b) => (a.lab_count || 0) - (b.lab_count || 0),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => {
        // Default to true if is_active is undefined
        const status = isActive !== undefined ? isActive : true;
        return (
          <Tag color={status ? 'success' : 'default'}>
            {status ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => {
        const status = record.is_active !== undefined ? record.is_active : true;
        return status === value;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete training"
            description="Are you sure you want to delete this training?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/super-admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Training Management</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>Training Management</Title>
            <Text type="secondary">Create and manage training programs</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleCreate}
          >
            Create Training
          </Button>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={trainings}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} trainings`,
            }}
          />
        </Card>
      </Content>

      <Modal
        title={editingTraining ? 'Edit Training' : 'Create New Training'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText={editingTraining ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Training Name"
            rules={[{ required: true, message: 'Please enter training name' }]}
          >
            <Input placeholder="e.g., Advanced Cybersecurity Training" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={4}
              placeholder="Describe the training program, objectives, and outcomes..."
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Training Type"
            rules={[{ required: true, message: 'Please select training type' }]}
          >
            <Select placeholder="Select training type">
              <Select.Option value="self_paced">Self-Paced</Select.Option>
              <Select.Option value="classroom">Classroom</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active Status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
